import type {
	BoundActions,
	HookActionsFunction,
	HookFunction,
	HookStateFunction,
	StoreActionApi,
} from 'react-sweet-state';
import { createStore, createHook, createActionsHook, createStateHook } from 'react-sweet-state';

import type {
	UnreadCommentsState,
	UnreadCommentIDSetOperation,
} from '../types/unreadCommentsTypes.ts';

const initialState = {
	// isUpdatingUnreadComments tracks when we're updating the unread comments set
	// (e.g., during initial render or when manually updating the set).
	isUpdatingUnreadComments: true,

	// isUnreadQueryLoading tracks when we're actively fetching comments from the server.
	isUnreadQueryLoading: false,

	unreadCommentIDSet: new Set<string>(), // list of unread comment IDs, including replies
	shouldClearVisibleThreads: false, // used in unread view in comments panel
};

const actions = {
	setIsUpdatingUnreadComments:
		(isUpdatingUnreadComments: boolean) =>
		({ setState }: StoreActionApi<UnreadCommentsState>): void => {
			setState({ isUpdatingUnreadComments });
		},

	setIsUnreadQueryLoading:
		(loading: boolean) =>
		({ setState }: StoreActionApi<UnreadCommentsState>): void => {
			setState({ isUnreadQueryLoading: loading });
		},
	updateUnreadCommentIDSet:
		(unreadCommentIDSet: Set<string>, operation: UnreadCommentIDSetOperation = 'initialize') =>
		({ setState, getState }: StoreActionApi<UnreadCommentsState>): void => {
			const { unreadCommentIDSet: existingSet } = getState();
			switch (operation) {
				case 'initialize':
					setState({ unreadCommentIDSet });
					break;
				case 'add':
					setState({
						unreadCommentIDSet: new Set([...existingSet, ...unreadCommentIDSet]),
					});
					break;
				case 'remove':
					const newSet = new Set(existingSet);
					unreadCommentIDSet.forEach((id) => newSet.delete(id));
					setState({ unreadCommentIDSet: newSet });
					break;
				case 'removeAll':
					setState({ unreadCommentIDSet: new Set() });
					break;
			}
		},
	setShouldClearVisibleThreads:
		(shouldClearVisibleThreads: boolean) =>
		({ setState }: StoreActionApi<UnreadCommentsState>): void => {
			setState({ shouldClearVisibleThreads });
		},
};

const UnreadCommentsStore = createStore({
	initialState,
	actions,
	name: 'UnreadCommentsStore',
});

export const useUnreadComments: HookFunction<
	{
		// isUpdatingUnreadComments tracks when we're updating the unread comments set
		// (e.g., during initial render or when manually updating the set).
		isUpdatingUnreadComments: boolean;
		// isUnreadQueryLoading tracks when we're actively fetching comments from the server.
		isUnreadQueryLoading: boolean;
		unreadCommentIDSet: Set<string>;
		shouldClearVisibleThreads: boolean;
	},
	BoundActions<
		{
			// isUpdatingUnreadComments tracks when we're updating the unread comments set
			// (e.g., during initial render or when manually updating the set).
			isUpdatingUnreadComments: boolean;
			// isUnreadQueryLoading tracks when we're actively fetching comments from the server.
			isUnreadQueryLoading: boolean;
			unreadCommentIDSet: Set<string>;
			shouldClearVisibleThreads: boolean;
		},
		{
			setIsUpdatingUnreadComments: (
				isUpdatingUnreadComments: boolean,
			) => ({ setState }: StoreActionApi<UnreadCommentsState>) => void;
			setIsUnreadQueryLoading: (
				loading: boolean,
			) => ({ setState }: StoreActionApi<UnreadCommentsState>) => void;
			updateUnreadCommentIDSet: (
				unreadCommentIDSet: Set<string>,
				operation?: UnreadCommentIDSetOperation,
			) => ({ setState, getState }: StoreActionApi<UnreadCommentsState>) => void;
			setShouldClearVisibleThreads: (
				shouldClearVisibleThreads: boolean,
			) => ({ setState }: StoreActionApi<UnreadCommentsState>) => void;
		}
	>,
	void
> = createHook(UnreadCommentsStore);
export const useUnreadCommentsActions: HookActionsFunction<
	BoundActions<
		{
			// isUpdatingUnreadComments tracks when we're updating the unread comments set
			// (e.g., during initial render or when manually updating the set).
			isUpdatingUnreadComments: boolean;
			// isUnreadQueryLoading tracks when we're actively fetching comments from the server.
			isUnreadQueryLoading: boolean;
			unreadCommentIDSet: Set<string>;
			shouldClearVisibleThreads: boolean;
		},
		{
			setIsUpdatingUnreadComments: (
				isUpdatingUnreadComments: boolean,
			) => ({ setState }: StoreActionApi<UnreadCommentsState>) => void;
			setIsUnreadQueryLoading: (
				loading: boolean,
			) => ({ setState }: StoreActionApi<UnreadCommentsState>) => void;
			updateUnreadCommentIDSet: (
				unreadCommentIDSet: Set<string>,
				operation?: UnreadCommentIDSetOperation,
			) => ({ setState, getState }: StoreActionApi<UnreadCommentsState>) => void;
			setShouldClearVisibleThreads: (
				shouldClearVisibleThreads: boolean,
			) => ({ setState }: StoreActionApi<UnreadCommentsState>) => void;
		}
	>
> = createActionsHook(UnreadCommentsStore);
export const useUnreadCommentsState: HookStateFunction<
	{
		// isUpdatingUnreadComments tracks when we're updating the unread comments set
		// (e.g., during initial render or when manually updating the set).
		isUpdatingUnreadComments: boolean;
		// isUnreadQueryLoading tracks when we're actively fetching comments from the server.
		isUnreadQueryLoading: boolean;
		unreadCommentIDSet: Set<string>;
		shouldClearVisibleThreads: boolean;
	},
	void
> = createStateHook(UnreadCommentsStore);
